import { IEquipment } from '@frontend/equipment/types';
import { ErrorHandler } from '@frontend/error-handler';
import { CommonMessage } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { submitLendingReturnTransaction } from '../../utils/submit-lending-return-transaction';
import { toError } from '../../utils/to-error';
import { useScreen } from '../useScreen';

export const useEquipmentReturnLost = () => {
    const intl = useIntl();
    const location = useLocation();
    const { onBack, onSubmit, store, navigationState } = useScreen();

    const stateEquipment = (location.state as { equipment?: IEquipment } | null)?.equipment ?? null;

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoCreate: false,
        autoFetch: false
    });

    const { setPackages, syncPackages } = useWizardPackages({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        transactionContext
    });

    const { transactionWorkflowId, dropOffPackageWorkflowId } = useWizardSettings(store);

    // Surface missing workflow configuration up-front so the user is not led
    // through a flow that will only fail at submit time.
    const configError = !transactionWorkflowId || !dropOffPackageWorkflowId;

    const [product, setProduct] = useState<IProduct | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(!!stateEquipment);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [reloadToken, setReloadToken] = useState<number>(0);

    // Synchronous guard against double-submit. `setIsSubmitting(true)` only
    // lands after the next render, so a rapid double-tap on Submit can enter
    // `handleSubmit` twice before the disabled state propagates.
    const isSubmittingRef = useRef<boolean>(false);

    useEffect(() => {
        if (!stateEquipment) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setLoadError(false);

        ProductClient.fetchProduct(stateEquipment.account_id, stateEquipment.product_id)
            .then((fetched) => {
                if (cancelled) return;
                setProduct(fetched);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setLoadError(true);
                ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.PRODUCT.DEFAULT });
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [stateEquipment, reloadToken]);

    const retryLoad = useCallback(() => {
        setReloadToken((n) => n + 1);
    }, []);

    const isBusy = isLoading || isSubmitting;
    const submitDisabled = useMemo(() => !stateEquipment || isBusy || configError || loadError, [stateEquipment, isBusy, configError, loadError]);

    const handleSubmit = useCallback(async () => {
        if (submitDisabled || !stateEquipment) return;
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        const iot = navigationState.iot;
        if (!iot || !iot.account_id || !iot.spot_id) {
            isSubmittingRef.current = false;
            ErrorHandler.handleError({
                error: new Error('Missing IoT context for return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }
        const spotId: string = iot.spot_id;
        const iotWithSpot = { ...iot, spot_id: spotId };

        if (!transactionWorkflowId || !dropOffPackageWorkflowId) {
            isSubmittingRef.current = false;
            ErrorHandler.handleError({
                error: new Error('Missing workflow settings for return transaction'),
                entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await submitLendingReturnTransaction({
                iot: iotWithSpot,
                equipments: [stateEquipment],
                userId: stateEquipment.user_id ?? null,
                settings: { transactionWorkflowId, dropOffPackageWorkflowId },
                application: store?.functionality?.application,
                feature: store?.functionality?.feature,
                functionality_id: store?.functionality?.id,
                transactionContext,
                setPackages,
                syncPackages
            });

            if (result.status === 'no-slot') {
                setError(
                    intl.formatMessage({
                        id: 'kiosk.wizard.equipment-return-lost.no-slot',
                        defaultMessage: 'No available SLOT could be found for this item.'
                    })
                );
                return;
            }

            onSubmit();
        } catch (e: unknown) {
            ErrorHandler.handleError({ error: toError(e), entity_type: CommonMessage.OBJECTS.TRANSACTION.DEFAULT });
        } finally {
            setIsSubmitting(false);
            isSubmittingRef.current = false;
        }
    }, [
        submitDisabled,
        stateEquipment,
        navigationState.iot,
        transactionWorkflowId,
        dropOffPackageWorkflowId,
        transactionContext,
        setPackages,
        syncPackages,
        onSubmit,
        intl
    ]);

    return {
        equipment: stateEquipment,
        product,
        isLoading,
        isSubmitting,
        isBusy,
        configError,
        loadError,
        retryLoad,
        error,
        submitDisabled,
        resetError: () => setError(null),
        onBack,
        onSubmit: handleSubmit
    };
};
